import type { ReactNode } from "react";
import { cx } from "../presentation.tsx";

export interface TableColumn<T> {
  key: string;
  header: ReactNode;
  /** Cell content; defaults to String(row[key]). */
  render?: (row: T) => ReactNode;
  align?: "start" | "end" | "center";
  /** CSS width, e.g. "8rem" or "20%". */
  width?: string;
  /** Mark the column that names the row; its cells become row headers (<th scope="row">). */
  rowHeader?: boolean;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Required accessible name; shown above the table unless hideCaption. */
  caption: ReactNode;
  hideCaption?: boolean;
  /** Max height of the scroll area; the header sticks within it. */
  maxHeight?: string;
  /** Shown in place of the body when rows is empty. */
  empty?: ReactNode;
  /** Highlights a row (aria-selected is not used: this is a static table, not a grid). */
  isRowSelected?: (row: T) => boolean;
  className?: string;
}

export function Table<T>({ columns, rows, rowKey, caption, hideCaption, maxHeight, empty, isRowSelected, className }: TableProps<T>) {
  const cell = (col: TableColumn<T>, row: T) =>
    col.render ? col.render(row) : String((row as Record<string, unknown>)[col.key] ?? "");
  return (
    <div className={cx("pmu-table-wrap", className)} style={maxHeight ? { maxHeight } : undefined} tabIndex={0} role="region" aria-label={typeof caption === "string" ? caption : undefined}>
      <table className="pmu-table">
        <caption className={cx("pmu-table__caption", hideCaption && "pmu-sr-only")}>{caption}</caption>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} scope="col" style={c.width ? { width: c.width } : undefined} className={c.align && c.align !== "start" ? `pmu-align-${c.align}` : undefined}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="pmu-table__empty">{empty ?? "Nothing to show"}</td></tr>
          ) : rows.map((row) => (
            <tr key={rowKey(row)} className={isRowSelected?.(row) ? "pmu-table__row--selected" : undefined}>
              {columns.map((c) => {
                const Tag = c.rowHeader ? "th" : "td";
                return (
                  <Tag key={c.key} scope={c.rowHeader ? "row" : undefined} className={c.align && c.align !== "start" ? `pmu-align-${c.align}` : undefined}>
                    {cell(c, row)}
                  </Tag>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
